if (!customElements.get('td-ugc-videos')) {
  class TDUgcVideos extends HTMLElement {
    static EDGE_OFFSET = 24;

    static SELECTORS = {
      slider: '[data-td-ugc-slider]',
      slide: '[data-td-ugc-slide]',
      video: '[data-td-ugc-video] video',
      trigger: '[data-td-ugc-trigger]',
      quickAdd: '[data-td-ugc-quick-add]',
      pause: '[data-td-ugc-pause]',
      mute: '[data-td-ugc-mute]',
    };

    constructor() {
      super();
      this.emblaApi = null;
      this.pendingPlayIndex = null;
      this.controlInteraction = false;
      this.skipNextSettle = false;
      this.isInView = false;
      this.isReducedMotion = false;
      this.isDesignMode = false;
      this.handleEmblaInit = this.handleEmblaInit.bind(this);
      this.handleSelect = this.handleSelect.bind(this);
      this.handleSettle = this.handleSettle.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handleControlPointerDown = this.handleControlPointerDown.bind(this);
      this.handleReInit = this.handleReInit.bind(this);
      this.handleTrigger = this.handleTrigger.bind(this);
      this.handleVideoPlay = this.handleVideoPlay.bind(this);
      this.handleVideoPause = this.handleVideoPause.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.sliderElement = this.querySelector(TDUgcVideos.SELECTORS.slider);
      this.slideElements = Array.from(this.querySelectorAll(TDUgcVideos.SELECTORS.slide));
      this.videoElements = this.slideElements.map((slide) => slide.querySelector(TDUgcVideos.SELECTORS.video));

      if (!(this.sliderElement instanceof HTMLElement) || !this.slideElements.length) {
        return;
      }

      this.abortController = new AbortController();
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isReducedMotion = this.reduceMotionQuery.matches;
      this.isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

      this.sliderElement.addEventListener('embla:init', this.handleEmblaInit, {
        signal: this.abortController.signal,
      });
      this.reduceMotionQuery.addEventListener('change', this.handleMotionChange, {
        signal: this.abortController.signal,
      });
      this.addEventListener('click', this.handleTrigger, {
        signal: this.abortController.signal,
      });
      this.addEventListener('pointerdown', this.handleControlPointerDown, {
        capture: true,
        signal: this.abortController.signal,
      });
      document.addEventListener('shopify:block:select', this.handleBlockSelect, {
        signal: this.abortController.signal,
      });
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect, {
        signal: this.abortController.signal,
      });

      this.videoElements.forEach((video) => {
        if (!(video instanceof HTMLVideoElement)) {
          return;
        }

        video.muted = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        this.syncVideoState(video, false);
        video.addEventListener('play', this.handleVideoPlay, {
          signal: this.abortController.signal,
        });
        video.addEventListener('pause', this.handleVideoPause, {
          signal: this.abortController.signal,
        });
      });

      if (typeof IntersectionObserver === 'function') {
        this.observer = new IntersectionObserver(
          (entries) => {
            this.isInView = entries.some((entry) => entry.isIntersecting);

            if (this.isInView) {
              this.playActiveVideo();
            } else {
              this.pauseAllVideos();
            }
          },
          { rootMargin: '0px 0px 18% 0px', threshold: 0 }
        );
        this.observer.observe(this);
      } else {
        this.isInView = true;
      }

      if (this.sliderElement.emblaApi) {
        this.handleEmblaInit({ detail: { api: this.sliderElement.emblaApi } });
      }
    }

    disconnectedCallback() {
      this.unbindEmblaEvents();
      this.observer?.disconnect();
      this.observer = null;
      this.abortController?.abort();
      this.abortController = null;
      this.pauseAllVideos();
      this.emblaApi = null;
    }

    handleEmblaInit(event) {
      this.unbindEmblaEvents();
      this.emblaApi = event?.detail?.api || this.sliderElement?.emblaApi || null;

      if (!this.emblaApi) {
        return;
      }

      this.applyEdgeAlign();
      this.emblaApi.on('select', this.handleSelect);
      this.emblaApi.on('settle', this.handleSettle);
      this.emblaApi.on('pointerDown', this.handlePointerDown);
      this.emblaApi.on('reInit', this.handleReInit);
      this.updateActiveState();
      this.playActiveVideo();
    }

    // Inner slides center; the first/last card edges stop 24px from the viewport.
    applyEdgeAlign() {
      if (!this.emblaApi || !this.slideElements.length) {
        return;
      }

      const align = (viewSize, snapSize, index) => {
        const lastIndex = this.slideElements.length - 1;
        if (index !== 0 && index !== lastIndex) {
          return (viewSize - snapSize) / 2;
        }

        const gap = parseFloat(getComputedStyle(this.slideElements[0]).paddingInlineStart) || 0;

        if (index === 0) {
          return TDUgcVideos.EDGE_OFFSET;
        }
        return viewSize - TDUgcVideos.EDGE_OFFSET + gap - snapSize;
      };

      this.emblaApi.reInit({ align });
    }

    unbindEmblaEvents() {
      if (!this.emblaApi) {
        return;
      }

      this.emblaApi.off('select', this.handleSelect);
      this.emblaApi.off('settle', this.handleSettle);
      this.emblaApi.off('pointerDown', this.handlePointerDown);
      this.emblaApi.off('reInit', this.handleReInit);
    }

    handleSelect() {
      this.updateActiveState();
    }

    handleSettle() {
      if (this.skipNextSettle) {
        this.skipNextSettle = false;
        this.controlInteraction = false;
        return;
      }

      const activeIndex = this.getActiveIndex();
      const shouldPlayManually = this.pendingPlayIndex === activeIndex;
      this.pendingPlayIndex = null;

      if (shouldPlayManually) {
        this.playVideoAt(activeIndex, true);
      } else {
        this.playActiveVideo();
      }
    }

    handlePointerDown() {
      if (this.controlInteraction) {
        this.skipNextSettle = true;
        return;
      }

      this.pendingPlayIndex = null;
      this.pauseAllVideos();
    }

    handleReInit() {
      this.updateActiveState();
    }

    handleControlPointerDown(event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      const control = event.target.closest(
        `${TDUgcVideos.SELECTORS.pause}, ${TDUgcVideos.SELECTORS.mute}`
      );
      if (control instanceof HTMLButtonElement && this.contains(control)) {
        this.controlInteraction = true;
        event.stopPropagation();
      }
    }

    handleTrigger(event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      const quickAddTrigger = event.target.closest(TDUgcVideos.SELECTORS.quickAdd);
      if (quickAddTrigger instanceof HTMLElement && this.contains(quickAddTrigger)) {
        const source = document.getElementById(quickAddTrigger.dataset.tdQuickAddSourceId);
        const modal = document.querySelector('td-quick-add-modal');

        if (source instanceof HTMLElement && modal && typeof modal.open === 'function') {
          event.preventDefault();
          modal.open(source);
        }
        return;
      }

      const pauseTrigger = event.target.closest(TDUgcVideos.SELECTORS.pause);
      const pauseSlide = pauseTrigger?.closest(TDUgcVideos.SELECTORS.slide);
      const pauseVideo = pauseSlide?.querySelector(TDUgcVideos.SELECTORS.video);
      if (pauseTrigger instanceof HTMLButtonElement && pauseVideo instanceof HTMLVideoElement) {
        if (pauseVideo.paused) {
          pauseVideo.play().catch(() => {});
        } else {
          pauseVideo.pause();
        }
        this.controlInteraction = false;
        return;
      }

      const muteTrigger = event.target.closest(TDUgcVideos.SELECTORS.mute);
      const muteSlide = muteTrigger?.closest(TDUgcVideos.SELECTORS.slide);
      const muteVideo = muteSlide?.querySelector(TDUgcVideos.SELECTORS.video);
      if (muteTrigger instanceof HTMLButtonElement && muteVideo instanceof HTMLVideoElement) {
        muteVideo.muted = !muteVideo.muted;
        if (muteVideo.muted) {
          muteVideo.setAttribute('muted', '');
        } else {
          muteVideo.removeAttribute('muted');
        }
        this.syncVideoState(muteVideo, !muteVideo.paused);
        this.controlInteraction = false;
        return;
      }

      const trigger = event.target.closest(TDUgcVideos.SELECTORS.trigger);
      const slide = trigger?.closest(TDUgcVideos.SELECTORS.slide);

      if (!(trigger instanceof HTMLButtonElement) || !(slide instanceof HTMLElement)) {
        return;
      }

      const index = this.slideElements.indexOf(slide);
      if (index < 0) {
        return;
      }

      if (index !== this.getActiveIndex() && this.emblaApi) {
        this.pendingPlayIndex = index;
        this.pauseAllVideos();
        this.emblaApi.scrollTo(index);
        return;
      }

      this.playVideoAt(index, true, index === this.getActiveIndex());
    }

    handleVideoPlay(event) {
      const video = event.currentTarget;
      if (!(video instanceof HTMLVideoElement)) {
        return;
      }

      this.videoElements.forEach((otherVideo) => {
        if (otherVideo instanceof HTMLVideoElement && otherVideo !== video) {
          otherVideo.pause();
        }
      });
      this.syncVideoState(video, true);
    }

    handleVideoPause(event) {
      const video = event.currentTarget;
      if (video instanceof HTMLVideoElement) {
        this.syncVideoState(video, false);
      }
    }

    handleMotionChange(event) {
      this.isReducedMotion = event.matches;

      if (this.isReducedMotion) {
        this.pauseAllVideos();
      } else {
        this.playActiveVideo();
      }
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      const slide = this.slideElements.find((element) => element.dataset.tdBlockId === blockId);
      const index = slide ? this.slideElements.indexOf(slide) : -1;

      if (index < 0 || !this.emblaApi) {
        return;
      }

      this.pendingPlayIndex = null;
      this.emblaApi.scrollTo(index);
    }

    handleBlockDeselect() {
      this.pendingPlayIndex = null;
    }

    getActiveIndex() {
      return typeof this.emblaApi?.selectedScrollSnap === 'function' ? this.emblaApi.selectedScrollSnap() : 0;
    }

    updateActiveState() {
      const activeIndex = this.getActiveIndex();

      this.slideElements.forEach((slide, index) => {
        slide.dataset.tdUgcActive = index === activeIndex ? 'true' : 'false';
      });
    }

    playActiveVideo() {
      if (!this.emblaApi || !this.isInView || this.isReducedMotion || this.isDesignMode) {
        return;
      }

      this.playVideoAt(this.getActiveIndex());
    }

    playVideoAt(index, isManual = false, preserveMute = false) {
      const video = this.videoElements[index];

      if (!(video instanceof HTMLVideoElement)) {
        this.pauseAllVideos();
        return;
      }

      if (!isManual && (!this.isInView || this.isReducedMotion || this.isDesignMode)) {
        this.pauseAllVideos();
        return;
      }

      this.videoElements.forEach((otherVideo) => {
        if (otherVideo instanceof HTMLVideoElement && otherVideo !== video) {
          otherVideo.pause();
        }
      });

      if (!preserveMute) {
        video.muted = true;
        video.setAttribute('muted', '');
      }
      video.play().catch(() => {});
    }

    pauseAllVideos() {
      this.videoElements.forEach((video) => {
        if (video instanceof HTMLVideoElement) {
          video.pause();
        }
      });
    }

    syncVideoState(video, isPlaying) {
      const slide = video.closest(TDUgcVideos.SELECTORS.slide);
      if (!(slide instanceof HTMLElement)) {
        return;
      }

      slide.setAttribute('data-td-ugc-playing', isPlaying ? 'true' : 'false');
      slide.setAttribute('data-td-ugc-muted', video.muted ? 'true' : 'false');

      const pauseButton = slide.querySelector(TDUgcVideos.SELECTORS.pause);
      if (pauseButton instanceof HTMLButtonElement) {
        pauseButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
        pauseButton.setAttribute('aria-pressed', String(isPlaying));
      }

      const muteButton = slide.querySelector(TDUgcVideos.SELECTORS.mute);
      if (muteButton instanceof HTMLButtonElement) {
        muteButton.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        muteButton.setAttribute('aria-pressed', String(video.muted));
      }
    }
  }

  customElements.define('td-ugc-videos', TDUgcVideos);
}
